"use client";
import Link from "next/link";
import AnimatedText from "./AnimatedText";
import AnimatedBlock from "./AnimatedBlock";
import AnimatedGrid from "./AnimatedGrid";
import AnimatedLink from "./AnimatedLink";
import FilterDropdown from "./FilterDropdown";
import { useState } from "react";
import translations from "@/lib/translations";
import Squircle from "@/components/Squircle";

export default function ProjectGrid({ projects, industries, categories, lang }) {
	const [activeIndustry, setActiveIndustry] = useState(null);
	const [activeCategory, setActiveCategory] = useState(null);
	const [visibleProjects, setVisibleProjects] = useState(projects);
	const [gridClass, setGridClass] = useState("");

	const slugify = (text) => (typeof text === "string" ? text.toLowerCase().replace(/\s+/g, "-") : "");

	// JUSTE POUR GRILLE PROJET
	const handleFilterIndustry = (slug) => {
		setGridClass("fade-out");
		setActiveIndustry(slug);

		setTimeout(() => {
			applyFilters(slug, activeCategory);
			setGridClass("");
		}, 300);
	};

	const handleFilterCategory = (slug) => {
		setGridClass("fade-out");
		setActiveCategory(slug);

		setTimeout(() => {
			applyFilters(activeIndustry, slug);
			setGridClass("");
		}, 300);
	};

	const applyFilters = (industrySlug, categorySlug) => {
		let filtered = projects;

		// Filtrer par industrie
		if (industrySlug) {
			filtered = filtered.filter((p) => p.industry?.title && slugify(p.industry.title) === industrySlug);
		}

		// Filtrer par catégorie
		if (categorySlug) {
			filtered = filtered.filter((p) => p.categories?.some((cat) => cat.title && slugify(cat.title) === categorySlug));
		}

		// Trier par date décroissante
		const sorted = filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
		setVisibleProjects(sorted);
	};

	const usedIndustryIds = new Set();
	const industryProjectCounts = new Map();

	projects.forEach((p) => {
		if (p.industry?._id) {
			usedIndustryIds.add(p.industry._id);
			const count = industryProjectCounts.get(p.industry._id) || 0;
			industryProjectCounts.set(p.industry._id, count + 1);
		}
	});
	const filteredIndustries = industries.filter((ind) => usedIndustryIds.has(ind._id));

	// Compter les catégories utilisées
	const usedCategoryIds = new Set();
	const categoryProjectCounts = new Map();

	projects.forEach((p) => {
		if (p.categories && Array.isArray(p.categories)) {
			p.categories.forEach((cat) => {
				if (cat._id) {
					usedCategoryIds.add(cat._id);
					const count = categoryProjectCounts.get(cat._id) || 0;
					categoryProjectCounts.set(cat._id, count + 1);
				}
			});
		}
	});
	const filteredCategories = categories.filter((cat) => usedCategoryIds.has(cat._id));

	// Calculer les counts croisés et les états disabled
	const getIndustryOptions = () => {
		let totalCount = 0;

		const options = filteredIndustries
			.map((ind) => {
				let count = 0;
				let disabled = false;

				if (activeCategory) {
					// Compter les projets qui ont cette industrie ET la catégorie active
					count = projects.filter((p) => p.industry?._id === ind._id && p.categories?.some((cat) => cat.title && slugify(cat.title) === activeCategory)).length;
					disabled = count === 0;
				} else {
					count = industryProjectCounts.get(ind._id) || 0;
				}

				totalCount += count;

				return {
					value: slugify(ind.title),
					label: ind.title,
					count,
					disabled,
				};
			})
			.sort((a, b) => a.label.localeCompare(b.label));

		// Si une catégorie est active, le "Tous" montre le nombre de projets avec cette catégorie
		const allCount = activeCategory ? projects.filter((p) => p.categories?.some((cat) => cat.title && slugify(cat.title) === activeCategory)).length : projects.length;

		return [{ value: null, label: translations.workpage.all[lang], count: allCount, disabled: false }, ...options];
	};

	const getCategoryOptions = () => {
		let totalCount = 0;

		const options = filteredCategories
			.map((cat) => {
				let count = 0;
				let disabled = false;

				if (activeIndustry) {
					// Compter les projets qui ont cette catégorie ET l'industrie active
					count = projects.filter((p) => p.categories?.some((c) => c._id === cat._id) && p.industry?.title && slugify(p.industry.title) === activeIndustry).length;
					disabled = count === 0;
				} else {
					count = categoryProjectCounts.get(cat._id) || 0;
				}

				totalCount += count;

				return {
					value: slugify(cat.title),
					label: cat.title,
					count,
					disabled,
				};
			})
			.sort((a, b) => a.label.localeCompare(b.label));

		// Si une industrie est active, le "Tous" montre le nombre de projets avec cette industrie
		const allCount = activeIndustry ? projects.filter((p) => p.industry?.title && slugify(p.industry.title) === activeIndustry).length : projects.length;

		return [{ value: null, label: translations.workpage.all[lang], count: allCount, disabled: false }, ...options];
	};

	const industryFilterOptions = getIndustryOptions();
	const categoryFilterOptions = getCategoryOptions();

	return (
		<div className="col-12">
			<AnimatedBlock className="filter">
				<div className="filters">
					<div className="filter-wrapper">
						<FilterDropdown activeFilter={activeIndustry} filters={industryFilterOptions} onFilterChange={handleFilterIndustry} label={translations.industry[lang]} />
						<FilterDropdown activeFilter={activeCategory} filters={categoryFilterOptions} onFilterChange={handleFilterCategory} label={translations.products[lang]} />
					</div>
				</div>
			</AnimatedBlock>
			<AnimatedGrid className={`grid ${gridClass}`}>
				{visibleProjects.map((project) => (
					<AnimatedLink key={project._id} href={`/${lang}/work/${project.slug_shared}`}>
						<article className="project">
							<Squircle size="card"></Squircle>
							<div className="thumbnail">
								<Squircle size="inner-card"></Squircle>
								{project.thumbnail && <img src={`${project.thumbnail.asset.url}?format=webp`} alt="" className="thumb" loading="lazy" />}
							</div>
							<div className="infos">
								<h3 className="title">{project.title}</h3>
								<ul className="meta">
									{project.categories?.slice(0, 6).map((c, i) => (
										<li key={i}>{c.title}</li>
									))}
									{project.categories?.length > 6 && <li>...</li>}
								</ul>
							</div>
						</article>
					</AnimatedLink>
				))}
			</AnimatedGrid>
		</div>
	);
}
